import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 md:pt-40 min-h-screen max-w-[1440px] mx-auto px-6 md:px-10 pb-24 grid md:grid-cols-12 gap-10" data-testid="forgot-password-page">
      <div className="md:col-span-6">
        <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Ch. 11 — Recovery</p>
        <h1 className="font-serif text-6xl md:text-8xl tracking-tight leading-none text-[#1A1714] mt-3">
          Forgot your<br /><em className="font-light">password?</em>
        </h1>
        <p className="font-serif text-xl mt-8 text-[#1A1714]/70 max-w-md">
          Enter your email and we'll send a link to reset it. The link stays good for one hour.
        </p>
      </div>

      <div className="md:col-span-6 md:pl-10">
        {sent ? (
          <div className="max-w-md border border-[#E5E1D8] p-8" data-testid="forgot-password-sent">
            <MailCheck size={36} className="text-[#7A2021]" />
            <h2 className="font-serif text-3xl mt-4 text-[#1A1714]">Check your inbox.</h2>
            <p className="font-serif text-lg mt-3 text-[#1A1714]/70">
              If an account exists for <em>{email}</em>, we've sent a reset link.
              It may take a minute to arrive — also check your spam folder.
            </p>
            <Link
              to="/login"
              data-testid="forgot-back-to-login"
              className="mt-8 inline-block border border-[#1A1714] px-6 py-3 font-mono text-[11px] uppercase tracking-editorial hover:bg-[#1A1714] hover:text-[#F9F6F0] transition-colors"
            >
              Back to sign in →
            </Link>
          </div>
        ) : (
          <>
            <form onSubmit={submit} data-testid="forgot-password-form" className="space-y-5 max-w-md">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">Email</label>
                <Input
                  data-testid="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 rounded-none border-[#E5E1D8] h-12"
                />
              </div>
              <Button
                data-testid="forgot-submit"
                type="submit"
                disabled={loading}
                className="w-full rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial h-12"
              >
                {loading ? "Sending…" : "Send reset link →"}
              </Button>
            </form>
            <p className="mt-8 font-mono text-xs text-[#1A1714]/60 max-w-md">
              Remembered it?{" "}
              <Link to="/login" data-testid="forgot-to-login" className="border-b border-[#1A1714] hover:text-[#7A2021] hover:border-[#7A2021]">
                Sign in →
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
